import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Inbox, Calendar as CalIcon, Phone, Mail, ExternalLink } from "lucide-react";

import Logo from "@/components/Logo";
import { apiGet, clearToken, getToken } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "bookings", label: "Bookings" },
  { id: "messages", label: "Messages" },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ bookings: 0, contact_messages: 0 });
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      nav("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const [meData, statsData, bookingsData, contactData] = await Promise.all([
          apiGet("/auth/me"),
          apiGet("/admin/stats"),
          apiGet("/admin/bookings"),
          apiGet("/admin/contact"),
        ]);
        setMe(meData);
        setStats(statsData);
        setBookings(bookingsData);
        setMessages(contactData);
      } catch (err) {
        if (err.response?.status === 401) {
          clearToken();
          nav("/login", { replace: true });
        } else {
          toast.error("Failed to load dashboard");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const logout = () => {
    clearToken();
    toast.success("Signed out");
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#14171A] text-white" data-testid="admin-page">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[#A8AEB2]/15 bg-[#14171A]/85 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="/" data-testid="admin-brand-link">
            <Logo />
          </a>
          <div className="flex items-center gap-4">
            {me && (
              <span className="hidden sm:inline-block font-body text-sm text-[#A8AEB2]">
                {me.email}
              </span>
            )}
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-sm border border-[#A8AEB2]/20 px-4 py-2 font-head text-sm uppercase tracking-wide text-white transition-colors hover:border-[#7AB648] hover:text-[#7AB648] active:scale-95"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <span className="font-body text-sm uppercase tracking-[0.4em] text-[#7AB648]">
              Dashboard
            </span>
            <h1 className="font-display uppercase text-5xl md:text-6xl leading-[0.9] mt-3">
              Enquiries
            </h1>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          <StatCard
            icon={<CalIcon size={20} />}
            label="Total Bookings"
            value={stats.bookings}
            testid="stat-bookings"
          />
          <StatCard
            icon={<Inbox size={20} />}
            label="Total Messages"
            value={stats.contact_messages}
            testid="stat-messages"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#A8AEB2]/15" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 font-head font-bold uppercase text-sm tracking-wide transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? "border-[#7AB648] text-white"
                  : "border-transparent text-[#A8AEB2] hover:text-white"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#A8AEB2]" data-testid="admin-loading">
            <Loader2 className="animate-spin mr-3" size={20} /> Loading…
          </div>
        ) : tab === "bookings" ? (
          <BookingsTable rows={bookings} />
        ) : (
          <MessagesTable rows={messages} />
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div
      className="rounded-sm border border-[#A8AEB2]/15 bg-[#1E2227] p-6 flex items-center justify-between"
      data-testid={testid}
    >
      <div>
        <p className="font-body text-xs uppercase tracking-[0.3em] text-[#A8AEB2]">{label}</p>
        <p className="font-display text-5xl mt-2 text-white leading-none">{value}</p>
      </div>
      <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#7AB648]/15 text-[#7AB648]">
        {icon}
      </span>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-sm border border-dashed border-[#A8AEB2]/20 bg-[#1E2227] py-16 text-center text-[#A8AEB2]" data-testid="admin-empty">
      {text}
    </div>
  );
}

function BookingsTable({ rows }) {
  if (!rows.length) return <EmptyState text="No booking requests yet." />;
  return (
    <div className="space-y-4" data-testid="admin-bookings-list">
      {rows.map((b) => (
        <div
          key={b.id}
          className="rounded-sm border border-[#A8AEB2]/15 bg-[#1E2227] p-5 md:p-6"
          data-testid={`admin-booking-${b.id}`}
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center flex-wrap gap-3">
                <h3 className="font-head font-bold text-xl text-white tracking-tight">
                  {b.name}
                </h3>
                <span className="rounded-sm bg-[#7AB648]/15 text-[#7AB648] px-2.5 py-1 text-xs font-head uppercase tracking-wider">
                  {b.service}
                </span>
              </div>
              <p className="mt-2 text-sm text-[#A8AEB2]">
                {formatDate(b.created_at)}
                {b.preferred_date && ` · Preferred: ${b.preferred_date}`}
              </p>
              {b.message && (
                <p className="mt-3 text-white/90 leading-relaxed">{b.message}</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
              <a
                href={`mailto:${b.email}`}
                className="inline-flex items-center gap-2 rounded-sm border border-[#A8AEB2]/20 px-4 py-2 text-sm text-white transition-colors hover:border-[#7AB648]"
              >
                <Mail size={14} /> {b.email}
              </a>
              <a
                href={`tel:${b.phone}`}
                className="inline-flex items-center gap-2 rounded-sm border border-[#A8AEB2]/20 px-4 py-2 text-sm text-white transition-colors hover:border-[#7AB648]"
              >
                <Phone size={14} /> {b.phone}
              </a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MessagesTable({ rows }) {
  if (!rows.length) return <EmptyState text="No contact messages yet." />;
  return (
    <div className="space-y-4" data-testid="admin-messages-list">
      {rows.map((c) => (
        <div
          key={c.id}
          className="rounded-sm border border-[#A8AEB2]/15 bg-[#1E2227] p-5 md:p-6"
          data-testid={`admin-message-${c.id}`}
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-head font-bold text-xl text-white tracking-tight">
                {c.name}
              </h3>
              <p className="mt-2 text-sm text-[#A8AEB2]">{formatDate(c.created_at)}</p>
              <p className="mt-3 text-white/90 leading-relaxed whitespace-pre-line">{c.message}</p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
              <a
                href={`mailto:${c.email}`}
                className="inline-flex items-center gap-2 rounded-sm border border-[#A8AEB2]/20 px-4 py-2 text-sm text-white transition-colors hover:border-[#7AB648]"
              >
                <Mail size={14} /> {c.email}
                <ExternalLink size={12} className="opacity-60" />
              </a>
              {c.phone && (
                <a
                  href={`tel:${c.phone}`}
                  className="inline-flex items-center gap-2 rounded-sm border border-[#A8AEB2]/20 px-4 py-2 text-sm text-white transition-colors hover:border-[#7AB648]"
                >
                  <Phone size={14} /> {c.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
